import { useCallback, useMemo, useState } from 'react';
import { noop } from 'lodash';
import { useRouter } from 'next/navigation';
import { useActiveAccountId } from '@frameio/shared/contexts';
import { getLink } from '@frameio/shared/routing';
import type { TrackingSourceType } from '@frameio/shared/segment/hooks/utils/trackingUtils';
import type { CreateSuccessCallback } from '../../operations';
import {
  CreateWorkspaceModal,
  type CreateWorkspaceModalProps,
} from '../components/WorkspaceModal/CreateWorkspaceModal';

export type ShowCreateWorkspaceModalInput = {
  onRequestClose?: () => void;
  trackingSource: TrackingSourceType;
};

export type UseCreateWorkspaceModalReturn = {
  createWorkspaceModal: React.ReactNode;
  showCreateWorkspaceModal: (input: ShowCreateWorkspaceModalInput) => void;
  hideCreateWorkspaceModal: () => void;
};

export const useCreateWorkspaceModal = (): UseCreateWorkspaceModalReturn => {
  const accountId = useActiveAccountId();
  const router = useRouter();

  const [props, setProps] = useState<
    Omit<CreateWorkspaceModalProps, 'accountId'>
  >({
    onRequestClose: noop,
    isShowing: false,
    trackingSource: '',
  });

  const onCreateWorkspaceSuccess = useCallback<CreateSuccessCallback>(
    ({ id }) => router.push(getLink({ page: 'workspaces', workspaceId: id })),
    [router]
  );

  const createWorkspaceModal = useMemo(
    () =>
      accountId ? (
        <CreateWorkspaceModal
          {...props}
          accountId={accountId}
          onCreateSuccess={onCreateWorkspaceSuccess}
        />
      ) : null,
    [props, accountId, onCreateWorkspaceSuccess]
  );

  const hideCreateWorkspaceModal = useCallback(() => {
    setProps((prev) => ({
      ...prev,
      isShowing: false,
      onRequestClose: noop,
      trackingSource: '',
    }));
  }, []);

  const showCreateWorkspaceModal = useCallback(
    ({
      onRequestClose = hideCreateWorkspaceModal,
      trackingSource,
    }: ShowCreateWorkspaceModalInput) => {
      if (!accountId) {
        throw new Error('An active account is required to create a workspace.');
      }
      setProps((prev) => ({
        ...prev,
        isShowing: true,
        onRequestClose,
        trackingSource,
      }));
    },
    [accountId, hideCreateWorkspaceModal]
  );

  return {
    createWorkspaceModal,
    showCreateWorkspaceModal,
    hideCreateWorkspaceModal,
  };
};
